import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import type { TemplateVariables } from '../../lib/types/template.types';

/**
 * Template UI State Management
 * Handles modals, view preferences, and transient UI state
 * Only persists user preferences (view mode, sidebar visibility)
 */
interface TemplateState {
  // Modal states
  createModalOpen: boolean;
  editingTemplateId: string | null;
  deleteConfirmId: string | null;
  categoryModalOpen: boolean;

  // Template picker (in WhatsApp chat)
  pickerOpen: boolean;
  selectedTemplateId: string | null;

  // Send flow
  sendModalOpen: boolean;
  templateToSend: string | null;
  editedVariables: TemplateVariables;

  // UI preferences (persisted)
  viewMode: 'grid' | 'list';
  sidebarVisible: boolean;
  favoritesBarVisible: boolean;

  // Actions - Modals
  openCreateModal: () => void;
  closeCreateModal: () => void;
  openEditModal: (id: string) => void;
  closeEditModal: () => void;
  openDeleteConfirm: (id: string) => void;
  closeDeleteConfirm: () => void;
  openCategoryModal: () => void;
  closeCategoryModal: () => void;

  // Actions - Template Picker
  openPicker: () => void;
  closePicker: () => void;
  selectTemplate: (id: string) => void;
  clearSelection: () => void;

  // Actions - Send Flow
  openSendModal: (templateId: string) => void;
  closeSendModal: () => void;
  setEditedVariables: (vars: TemplateVariables) => void;
  clearEditedVariables: () => void;

  // Actions - UI Preferences
  toggleViewMode: () => void;
  setViewMode: (mode: 'grid' | 'list') => void;
  toggleSidebar: () => void;
  toggleFavoritesBar: () => void;
}

export const useTemplateStore = create<TemplateState>()(
  persist(
    immer((set) => ({
      // Initial state
      createModalOpen: false,
      editingTemplateId: null,
      deleteConfirmId: null,
      categoryModalOpen: false,

      pickerOpen: false,
      selectedTemplateId: null,

      sendModalOpen: false,
      templateToSend: null,
      editedVariables: {},

      viewMode: 'grid',
      sidebarVisible: true,
      favoritesBarVisible: true,

      // Modal actions
      openCreateModal: () => set((state) => { state.createModalOpen = true; }),
      closeCreateModal: () => set((state) => { state.createModalOpen = false; }),

      openEditModal: (id) => set((state) => {
        state.editingTemplateId = id;
      }),
      closeEditModal: () => set((state) => {
        state.editingTemplateId = null;
      }),

      openDeleteConfirm: (id) => set((state) => {
        state.deleteConfirmId = id;
      }),
      closeDeleteConfirm: () => set((state) => {
        state.deleteConfirmId = null;
      }),

      openCategoryModal: () => set((state) => {
        state.categoryModalOpen = true;
      }),
      closeCategoryModal: () => set((state) => {
        state.categoryModalOpen = false;
      }),

      // Template picker actions
      openPicker: () => set((state) => { state.pickerOpen = true; }),
      closePicker: () => set((state) => {
        state.pickerOpen = false;
        state.selectedTemplateId = null;
      }),

      selectTemplate: (id) => set((state) => {
        state.selectedTemplateId = id;
      }),
      clearSelection: () => set((state) => {
        state.selectedTemplateId = null;
      }),

      // Send flow actions
      openSendModal: (templateId) => set((state) => {
        state.sendModalOpen = true;
        state.templateToSend = templateId;
      }),
      closeSendModal: () => set((state) => {
        state.sendModalOpen = false;
        state.templateToSend = null;
        state.editedVariables = {};
      }),

      setEditedVariables: (vars) => set((state) => {
        state.editedVariables = vars;
      }),
      clearEditedVariables: () => set((state) => {
        state.editedVariables = {};
      }),

      // UI preference actions
      toggleViewMode: () => set((state) => {
        state.viewMode = state.viewMode === 'grid' ? 'list' : 'grid';
      }),
      setViewMode: (mode) => set((state) => {
        state.viewMode = mode;
      }),

      toggleSidebar: () => set((state) => {
        state.sidebarVisible = !state.sidebarVisible;
      }),
      toggleFavoritesBar: () => set((state) => {
        state.favoritesBarVisible = !state.favoritesBarVisible;
      }),
    })),
    {
      name: 'template-ui-state',
      // Only persist user preferences
      partialize: (state) => ({
        viewMode: state.viewMode,
        sidebarVisible: state.sidebarVisible,
        favoritesBarVisible: state.favoritesBarVisible,
      }),
    }
  )
);
